import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { t } from '@sm/intl';
import { Box } from '@wds/box';

import COPY from '~pricing/components/PricingFooter/copy';
import FrequentlyAskedQuestion from '~pricing/components/PricingFooter/components/FAQ';
import FAQAnswer from '~pricing/components/PricingFooter/components/FAQAnswer';

import usePricingFooterStyles from '~pricing/components/PricingFooter/useStyles';
import { usePricingState } from '~shared/hooks/usePricingState';
import { Packages } from '~shared/constants/pricing';
import usePriceDisplay from '~app/pages/Pricing/hooks/usePriceDisplay';
import { PackageSkuCost } from '~shared/types/pricing/pricingsvc';

import { MoreAboutPropType } from './types';
import experimentCopy from './experimentCopy';

export default function MoreAbout({
  displayEduFaq,
  displayTeamsDiscountFAQ,
  teamsDiscountPercent,
  pageUtSource,
  utSource2,
  utSource3,
}: MoreAboutPropType): ReactElement {
  const { PricingList } = usePricingFooterStyles();
  const {
    state: {
      packages: { allPackages },
    },
  } = usePricingState();

  const formsAnnual = allPackages.find(pkg => pkg.packageName === Packages.FormsAnnual);
  const formsMonthly = allPackages.find(pkg => pkg.packageName === Packages.FormsMonthly);

  const { monthlyFormattedCost: formsAnnualCost } = usePriceDisplay(
    Packages.FormsAnnual,
    (formsAnnual?.skuCost ?? []) as PackageSkuCost[]
  );
  const { monthlyFormattedCost: formsMonthlyCost } = usePriceDisplay(
    Packages.FormsMonthly,
    (formsMonthly?.skuCost ?? []) as PackageSkuCost[]
  );

  return (
    <Box className={PricingList} data-testid="MoreAbout__Section" role="list">
      {displayEduFaq && (
        <FrequentlyAskedQuestion
          data-testid="MoreAbout__EduPricing"
          heading={t(COPY.SPECIAL_PRICING_FOR_EDUCATORS_FAQ)}
        >
          <FAQAnswer>
            {t(COPY.SPECIAL_PRICING_FOR_EDUCATORS_ANSWER)}{' '}
            <Link to={`/pricing/educational/?ut_source=${pageUtSource}`}>{t(COPY.SEE_PRICING)}</Link>
          </FAQAnswer>
        </FrequentlyAskedQuestion>
      )}
      <FrequentlyAskedQuestion data-testid="MoreAbout__FormsPricing" heading={t(experimentCopy.grow_2529_can_i_build)}>
        <FAQAnswer>
          {t(experimentCopy.grow_2529_all_our_plans, {
            monthlyCost: formsMonthlyCost,
            annualMonthlyCost: formsAnnualCost,
          })}
        </FAQAnswer>
      </FrequentlyAskedQuestion>
      {displayTeamsDiscountFAQ && (
        <FrequentlyAskedQuestion heading={t(COPY.DISCOUNTS_FOR_TEAMS_FAQ)}>
          <FAQAnswer>
            {t(COPY.DISCOUNTS_FOR_TEAMS_ANSWER, { teamsMinimumDiscount: teamsDiscountPercent })}{' '}
            <Link to={`/pricing/teams/details/?ut_source=${pageUtSource}`}>{t(COPY.SEE_PRICING)}</Link>
          </FAQAnswer>
        </FrequentlyAskedQuestion>
      )}

      <FrequentlyAskedQuestion heading={t(COPY.CORPORATE_PLANS_FAQ)}>
        <FAQAnswer>
          {t(COPY.CORPORATE_PLANS_ANSWER)}{' '}
          <a
            href={`/mp/contact-sales/?ut_source=pricing_page&ut_source2=${utSource2}&ut_source3=${utSource3}`}
            target="_blank"
            rel="noreferrer"
          >
            {t(COPY.CONTACT_SALES)}
          </a>
        </FAQAnswer>
      </FrequentlyAskedQuestion>
    </Box>
  );
}
